import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Container, Tabs, Title, Button, Group } from "@mantine/core";
import { GenevaFormGenerator } from "./components/GenevaFormGenerator";
import { useFirestore } from "../../helpers/firebaseContext";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { MyUserDataContext } from "../../contexts";
import { CustomClaims } from "../../types";
import { SchemaType } from "../../types/schema";
import { DEFAULT_SCHEMA_TYPE } from "../../utils/schema-config";
import { getSchemaDefaults } from "../../utils/geneva-schema-loader";
import { History } from "../../components/history";



//create Geneva Request component
export default function GenevaRequest() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();

  // Get orgId and templateId from URL params or defaults
  const orgId = searchParams.get('orgId') || 'demo';
  const rawSchemaType = searchParams.get('schemaType');
  const initialSchemaType: SchemaType = rawSchemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
  const templateParam = searchParams.get('templateId');
  const defaultTemplateId = initialSchemaType === 'gwi' ? 'gwi' : 'default';
  const templateId = templateParam || defaultTemplateId;
  const [schemaType, setSchemaType] = useState<SchemaType>(initialSchemaType);
  const schemaDefaults = useMemo(() => getSchemaDefaults(schemaType), [schemaType]);
  const schemaLabel = schemaDefaults.displayName;
  const db = useFirestore();
  const userContext = useContext(MyUserDataContext);

  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canCreate = Boolean(customClaims.geneva_requests_create);

  const initialTab = (searchParams.get('tab') as string) || 'details';
  const safeInitialTab = !canCreate && initialTab === 'edit' ? 'details' : initialTab;
  const [activeTab, setActiveTab] = useState<string>(safeInitialTab);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const navigate = useNavigate();
  const readOnly = isApproved || !canCreate;

  useEffect(() => {
    const nextType: SchemaType = rawSchemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
    if (nextType !== schemaType) {
      setSchemaType(nextType);
    }
  }, [rawSchemaType, schemaType]);

  // This page should only be used for viewing/editing existing requests
  if (!id) {
    navigate('/geneva_requests');
    return null;
  }

  // Load request status to guard editing
  useEffect(() => {
    if (!db || !id) return;
    const ref = doc(db, schemaDefaults.requestCollection, id);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as any;
        setIsApproved(data?.status === 'approved');
        const docSchemaType: SchemaType = data?.schemaType === 'gwi' ? 'gwi' : DEFAULT_SCHEMA_TYPE;
        if (docSchemaType !== schemaType) {
          setSchemaType(docSchemaType);
          const params = new URLSearchParams(searchParamsKey);
          params.set('schemaType', docSchemaType);
          params.set('orgId', orgId);
          const nextTemplateId = (data?.templateId as string | undefined) || (docSchemaType === 'gwi' ? 'gwi' : 'default');
          params.set('templateId', nextTemplateId);
          setSearchParams(params);
        }
      }
    });
    return () => unsub();
  }, [db, id, schemaDefaults.requestCollection, schemaType, orgId, templateId, searchParamsKey, setSearchParams]);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>{schemaLabel} Request #{id}</Title>
        <Button
          variant="subtle"
          onClick={() => navigate('/geneva_requests')}
        >
          Back to Requests
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'details')}>
        <Tabs.List>
          <Tabs.Tab value="details">Request Details</Tabs.Tab>
          <Tabs.Tab value="edit" disabled={readOnly}>Edit</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>

        {/* Request Details */}
        <Tabs.Panel value="details" pt="md">
          <Box>
            {/* TODO: Show specific request details */}
            Request details for ID: {id}
          </Box>
        </Tabs.Panel>

        {/* Edit Request */}
        <Tabs.Panel value="edit" pt="md">
          <GenevaFormGenerator
            orgId={orgId}
            templateId={templateId}
            requestId={id}
            readonly={readOnly}
            schemaType={schemaType}
          />
        </Tabs.Panel>

        {/* Request History */}
        <Tabs.Panel value="history" pt="md">
          <Box>
            {db && id && (
              <History historyCollection={collection(db, schemaDefaults.requestCollection, id, 'history')} />
            )}
          </Box>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
